/**
 * The printable A4 clinical report.
 *
 * A standalone document builder rather than a closure inside the console,
 * because it is the one artefact of this feature that leaves the screen: it
 * gets exported to PDF, filed in a record, and handed to a colleague. That
 * deserves to be testable and previewable on its own, and it kept the console
 * component from growing a 250-line string literal in the middle of it.
 *
 * Two rules the document obeys, both learned from the first export:
 *
 * 1. **Only findings are printed.** The first version printed the raw stream,
 *    and a reasoning model on the fallback ladder emits its chain of thought as
 *    ordinary `delta.content` — the resulting PDF was five pages of the model
 *    talking to itself in English with the Arabic report buried inside it, and
 *    a tail of degenerated tokens after that. `parseReport` filters by the
 *    severity contract; this renders only what survives.
 *
 * 2. **`@page { margin: 0 }`.** With any non-zero page margin, Chrome fills it
 *    with its own header and footer — the source URL, the print date, "1/5".
 *    That is what made the first export read as a printed web page rather than
 *    a clinical document. The margin lives inside `.sheet` instead.
 */

/** Print goes through document.write, so every model-authored string is escaped. */
export function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** A model id, shortened to the part a human reads. */
export const shortModel = (model) => {
  if (!model) return '—';
  const withoutVendor = model.includes('/') ? model.split('/')[1] : model;
  return withoutVendor.replace(':free', '').replace(/-/g, ' ');
};

export const TONE_LABEL = {
  critical: 'حرج',
  warning: 'يستدعي الانتباه',
  minor: 'ملاحظة',
  gap: 'فجوة في البيانات',
  action: 'الإجراء المقترح',
  summary: 'الخلاصة',
  notice: 'تنبيه',
  neutral: '',
};

/** Inline markdown segments -> print HTML. The report template uses **bold**. */
const inline = (segments) =>
  (segments || [])
    .map((segment) => {
      const safe = escapeHtml(segment.text);
      if (segment.bold) return `<strong>${safe}</strong>`;
      if (segment.code) return `<code>${safe}</code>`;
      return safe;
    })
    .join('');

/* ==========================================================================
   Styles
   ========================================================================== */

/**
 * Deliberately a light document with coloured rails rather than the console's
 * dark glass. The console is a screen the doctor looks at for thirty seconds;
 * this is paper. Dark panels print as a solid block of toner and make the
 * findings harder to read, not more impressive.
 */
const STYLES = `
  @page { size: A4; margin: 0; }

  * { box-sizing: border-box; }

  html, body {
    margin: 0;
    padding: 0;
    background: #fff;
    color: #16202b;
    font-family: 'Cairo', 'Segoe UI', 'Tahoma', 'Arial', sans-serif;
    font-size: 11.5pt;
    line-height: 1.75;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }

  .sheet { padding: 15mm 16mm 24mm; }

  /* --- masthead --- */
  .head {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding-bottom: 10px;
    border-bottom: 2px solid #16202b;
  }
  .head__mark {
    flex: none;
    width: 34px; height: 34px;
    border-radius: 8px;
    background: #0b6e6e;
    color: #fff;
    display: flex; align-items: center; justify-content: center;
    font-size: 17px;
  }
  .head__t { flex: 1; min-width: 0; }
  h1 { margin: 0; font-size: 15.5pt; font-weight: 700; letter-spacing: -.2px; }
  .head__sub { margin: 1px 0 0; font-size: 8.5pt; color: #64757f; letter-spacing: .04em; }
  .head__meta { text-align: left; font-size: 8pt; color: #64757f; line-height: 1.6; }
  .head__meta b { display: block; color: #16202b; font-size: 8.5pt; }

  /* --- patient band --- */
  .who {
    display: flex; flex-wrap: wrap;
    border: 1px solid #dbe3e6;
    border-radius: 4px;
    margin: 12px 0 14px;
    overflow: hidden;
  }
  .who > div { flex: 1 1 110px; padding: 7px 12px; border-inline-start: 1px solid #eef2f4; }
  .who > div:first-child { border-inline-start: 0; }
  .who .k { display: block; font-size: 7pt; letter-spacing: .12em; color: #8496a0; }
  .who .v { font-size: 10pt; font-weight: 600; }

  /* --- summary --- */
  .summary {
    border-inline-start: 4px solid #0b6e6e;
    background: #f1f8f8;
    border-radius: 0 5px 5px 0;
    padding: 11px 14px;
    margin-bottom: 14px;
    font-size: 11.5pt;
  }
  .summary__k {
    display: block;
    font-size: 7.5pt; letter-spacing: .13em;
    color: #0b6e6e; margin-bottom: 2px;
  }

  /* --- legend --- */
  .legend { display: flex; flex-wrap: wrap; gap: 14px; margin: 0 0 10px; font-size: 8pt; color: #64757f; }
  .lg { display: inline-flex; align-items: center; gap: 5px; }
  .lg__dot { width: 8px; height: 8px; border-radius: 2px; display: inline-block; }
  .lg__dot--critical { background: #d93a3a; }
  .lg__dot--warning  { background: #e07a1f; }
  .lg__dot--minor    { background: #d6a70a; }
  .lg__dot--gap      { background: #2b9fd6; }
  .lg__dot--action   { background: #16906a; }
  .lg__dot--neutral, .lg__dot--notice { background: #93a3ad; }

  /* --- findings --- */
  ul { list-style: none; margin: 0; padding: 0; }

  .f {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    padding: 9px 12px;
    margin-bottom: 6px;
    border: 1px solid #e6ecef;
    border-inline-start-width: 4px;
    border-radius: 0 5px 5px 0;
    /* A finding must never split across a page break — half a clinical
       statement at the foot of a page is worse than a short page. */
    break-inside: avoid;
    page-break-inside: avoid;
  }
  .f__n {
    flex: none;
    width: 19px; height: 19px;
    border-radius: 50%;
    background: #eef3f5;
    color: #55666f;
    font-size: 8pt; font-weight: 700;
    display: flex; align-items: center; justify-content: center;
    margin-top: 2px;
  }
  .f__body { flex: 1; min-width: 0; }
  .f__claim { display: block; font-weight: 600; font-size: 10.5pt; }
  .f__cause { display: block; margin-top: 1px; font-size: 9.5pt; color: #55666f; }
  .f__tag { flex: none; font-size: 7pt; letter-spacing: .08em; color: #8496a0; padding-top: 4px; white-space: nowrap; }
  .f code { font-family: 'Consolas', monospace; font-size: .9em; background: #f2f6f7; padding: 0 3px; border-radius: 2px; }

  .f--critical { border-inline-start-color: #d93a3a; background: #fdf5f5; }
  .f--critical .f__n { background: #f9e3e3; color: #a82626; }
  .f--warning  { border-inline-start-color: #e07a1f; background: #fdf8f2; }
  .f--warning  .f__n { background: #fbeada; color: #a4550e; }
  .f--minor    { border-inline-start-color: #d6a70a; background: #fdfbf1; }
  .f--minor    .f__n { background: #f8efcf; color: #97740a; }
  .f--gap      { border-inline-start-color: #2b9fd6; background: #f3fafd; }
  .f--gap      .f__n { background: #dcf0f9; color: #1c6d94; }
  .f--action   { border-inline-start-color: #16906a; background: #f2faf7; }
  .f--action   .f__n { background: #d8f0e6; color: #0f6349; }
  .f--neutral, .f--notice { border-inline-start-color: #93a3ad; background: #f8fafb; }

  /* --- footer, repeated on every printed page --- */
  .foot {
    position: fixed;
    bottom: 0; left: 0; right: 0;
    padding: 6px 16mm 8mm;
    border-top: 1px solid #e6ecef;
    background: #fff;
    font-size: 7.5pt;
    line-height: 1.55;
    color: #7b8b94;
  }
  .foot b { color: #55666f; }
`;

/* ==========================================================================
   The document
   ========================================================================== */

/**
 * Build the complete printable document.
 *
 * @param {object} input
 * @param {Array}  input.findings   From `parseReport().findings` — never raw stream text.
 * @param {string} [input.patientName]
 * @param {string} [input.patientCode]
 * @param {object} [input.meta]     The run's meta frame: model, span, counts.
 * @param {string} [input.tier]     'standard' | 'premium'
 * @param {Date}   [input.now]      Injectable, so the output is testable.
 * @returns {string} A full HTML document.
 */
export function buildReportDocument({
  findings = [],
  patientName,
  patientCode,
  meta = {},
  tier = 'standard',
  now = new Date(),
}) {
  // The summary heads the document; the rest are numbered so a doctor can
  // refer to "point 3" out loud.
  const summary = findings.find((f) => f.tone === 'summary');
  const body = findings.filter((f) => f.tone !== 'summary');
  const counts = meta.counts;

  const rows = body
    .map(
      (finding, index) => `
        <li class="f f--${finding.tone}">
          <span class="f__n">${index + 1}</span>
          <span class="f__body">
            <span class="f__claim">${inline(finding.claimSegments)}</span>
            ${finding.causeSegments ? `<span class="f__cause">${inline(finding.causeSegments)}</span>` : ''}
          </span>
          <span class="f__tag">${TONE_LABEL[finding.tone] || ''}</span>
        </li>`,
    )
    .join('');

  // Only the tones actually present. A legend listing severities that do not
  // appear invites the reader to hunt for findings that are not there.
  const legend = [...new Set(body.map((f) => f.tone))]
    .filter((tone) => TONE_LABEL[tone])
    .map((tone) => `<span class="lg"><i class="lg__dot lg__dot--${tone}"></i>${TONE_LABEL[tone]}</span>`)
    .join('');

  const stamp = now.toLocaleString('ar-EG', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

  const cell = (key, value) =>
    `<div><span class="k">${key}</span><span class="v">${value}</span></div>`;

  const band = [
    cell('المريض', escapeHtml(patientName || 'غير مسجل')),
    patientCode ? cell('الكود', escapeHtml(patientCode)) : '',
    counts ? cell('الروشتات', counts.prescriptions) : '',
    counts ? cell('مستندات خارجية', counts.externalRecords) : '',
    counts ? cell('الزيارات', counts.bookings) : '',
    meta.span ? cell('المدى الزمني', `<span dir="ltr">${escapeHtml(meta.span)}</span>`) : '',
  ]
    .filter(Boolean)
    .join('');

  return `<!doctype html><html dir="rtl" lang="ar"><head>
<meta charset="utf-8">
<title>تحليل الملف الطبي — ${escapeHtml(patientName || 'مريض')}</title>
<style>${STYLES}</style></head>
<body>
  <div class="sheet">
    <header class="head">
      <span class="head__mark">🩺</span>
      <div class="head__t">
        <h1>تحليل الملف الطبي</h1>
        <p class="head__sub">تقرير آلي — رصد المؤشرات غير الطبيعية</p>
      </div>
      <div class="head__meta">
        <b>${escapeHtml(stamp)}</b>
        ${escapeHtml(shortModel(meta.model))}${tier === 'premium' ? ' · تحليل متقدم' : ''}
      </div>
    </header>

    <section class="who">${band}</section>

    ${
      summary
        ? `<div class="summary"><span class="summary__k">الخلاصة</span>${inline(
            summary.claimSegments,
          )}${summary.causeSegments ? ` — ${inline(summary.causeSegments)}` : ''}</div>`
        : ''
    }

    ${legend ? `<div class="legend">${legend}</div>` : ''}

    <ul>${rows}</ul>
  </div>

  <footer class="foot">
    <b>أداة دعم قرار.</b>
    التقرير أعلاه ناتج عن نموذج ذكاء اصطناعي قرأ الملف المسجّل على المنصة فقط،
    ولا يُغني عن الفحص الإكلينيكي ولا يُعد تشخيصاً. القرار الطبي والمسؤولية على الطبيب المعالج.
  </footer>
</body></html>`;
}

export default buildReportDocument;
